import { Scene } from 'phaser';
import { Range } from 'immutable';
import { isCapacitorNative } from 'Utils/Constants';
import { doubleSharpUrl } from './doubleSharpGlyph';

export const phaserAssetsRootDir =
  /*process.env.REACT_APP_NODE_ENV === 'local'
    ? 'phaser'*/
  'https://assets.museflow.ai/phaser';

// MUS-435: hard upper bound on total preload time. A cold-cache stall can leave
// many of the ~70 assets never reaching a terminal state; the per-file XHR timeout
// plus the all-settle gate can still sum to minutes. This bounds the whole preload
// so first launch never hangs, regardless of how many assets stall.
const PRELOAD_TIMEOUT_MS = 15_000;

export class Preloader extends Scene {
  private preloadWatchdog?: ReturnType<typeof setTimeout>;
  private nextSceneStarted = false;

  constructor() {
    super('Preloader');
  }

  init() {
    const CLIP_TYPE_COUNT = 8;
    const CLIP_SUBTYPE_COUNT = 5;
    const clipDistribution = Range(1, CLIP_SUBTYPE_COUNT + 1)
      .sortBy(Math.random)
      .flatMap((i) =>
        Range(0, CLIP_TYPE_COUNT)
          .sortBy(Math.random)
          .map((j) => `feedback${j}_0${i}`)
          .toArray(),
      )
      .toArray();
    console.debug('seq', clipDistribution);
    this.registry.set('feedbackClipRandomDistribution', clipDistribution);
    this.registry.set('feedbackClipCount', CLIP_SUBTYPE_COUNT * CLIP_TYPE_COUNT);
    this.registry.set('clipIdx', 0);
  }

  preload() {
    // MUS-435: start the total-preload watchdog before queuing. If create() has not
    // run within the bound (a stall where assets never complete), force a degraded
    // start so first launch cannot hang. Cleared on the normal create() path and on
    // scene shutdown. setTimeout (not the Phaser clock) so it fires during the
    // loading phase regardless of scene-update state.
    this.preloadWatchdog = setTimeout(() => {
      console.error(
        `[Preloader] asset loading exceeded ${PRELOAD_TIMEOUT_MS}ms; starting in degraded mode`,
      );
      this.startNextScene();
    }, PRELOAD_TIMEOUT_MS);
    this.events.once('shutdown', () => this.clearPreloadWatchdog());
    // Queue files synchronously and let Phaser advance to create() after every
    // file reaches a terminal state. The game-level loader timeout bounds a
    // cold-cache request that otherwise never emits completion or error.
    const loadAsset = (_queuedLoader: Phaser.Loader.LoaderPlugin) => undefined;
    // MUS-435: seed each queued file's per-request XHR timeout from the loader's
    // global timeout. Phaser's MergeXHRSettings lets a File's default
    // XHRSettings.timeout (0) override the game-level loader.timeout, so without
    // this a stalled cold-cache fetch has no client-side timeout and wedges startup
    // forever. Copying the loader timeout onto each file as it is queued keeps the
    // per-file value non-zero, so the merge preserves the bound and a stalled fetch
    // terminates (error) -> create() proceeds in degraded mode.
    this.load.on(
      'addfile',
      (
        _key: string,
        _type: string,
        _loader: Phaser.Loader.LoaderPlugin,
        file: Phaser.Loader.File,
      ) => {
        if (file.xhrSettings) {
          file.xhrSettings.timeout = this.load.xhr.timeout;
        }
      },
    );
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.error(`[Preloader] Failed to load ${file.key}; continuing in degraded mode`);
    });
    //  Load the assets for the game - Replace with your own assets

    // SVG loading in phaser, by nature, rasterises the SVG into a bitmap texture so it can no longer
    // be scaled like a normal vector graphic. Therefore, while loading the SVG itself, we pass in a scale
    // property that rasterises the svg at a high enough resolution such that we dont need to scale it up
    // any further during gameplay, avoiding blurriness.

    // Because of retina displays rendering canvases at lower resolutions than the device, we need to
    // raster all svgs at a higher resolution to avoid blurriness. We do this by scaling up all of our other
    // scaling factors by the device pixel ratio for consistency across different hardware.
    const dpr = 1;
    // Rasterise svgs at a 2x size, in case they need to be scaled up during gameplay. Also account for the pixel
    // ratio as stated above by multiplying the scaling factor by the dpr.
    const scale = dpr * 2;

    // Down here is where we load our assets:-
    const load = () => {
      console.debug('loading assets from ', phaserAssetsRootDir);
      this.load.setPath(phaserAssetsRootDir + '/graphics');
      loadAsset(this.load.image('white_key', 'white_key.png'));
      loadAsset(this.load.image('black_key', 'black_key.png'));
      loadAsset(this.load.image('black_key_shadow', 'black_key_shadow.png'));
      loadAsset(this.load.image('white_key_overlay', 'white_key_overlay.png'));
      loadAsset(this.load.image('black_key_overlay', 'black_key_overlay.png'));
      loadAsset(this.load.svg('treble_clef', 'treble_clef.svg'));
      loadAsset(this.load.svg('bass_clef', 'bass_clef.svg'));
      loadAsset(this.load.svg('brace', 'brace.svg'));
      loadAsset(this.load.svg('accidental_sharp', 'sharp.svg'));
      loadAsset(this.load.svg('accidental_flat', 'flat.svg'));
      loadAsset(this.load.svg('accidental_natural', 'natural.svg'));
      // The double sharp ships in the app's own build, not on the asset host.
      loadAsset(this.load.svg('accidental_double_sharp', doubleSharpUrl()));
      for (let i = 1; i <= 9; i++) {
        loadAsset(this.load.svg(`time_sig_${i}`, `time_sig_${i}.svg`));
      }

      loadAsset(
        this.load.svg('half_note', 'half_note.svg', {
          scale: scale * 1.3,
        }),
      );

      loadAsset(
        this.load.svg('half_note_mask', 'half_note_mask.svg', {
          scale: scale * 1.3,
        }),
      );
      loadAsset(
        this.load.svg('quarter_note', 'quarter_note.svg', {
          scale: scale * 1.3,
        }),
      );
      loadAsset(
        this.load.svg('quarter_note_mask', 'quarter_note_mask.svg', {
          scale: scale * 1.3,
        }),
      );
      loadAsset(this.load.svg('whole_note', 'whole_note.svg', { scale: scale * 1.3 }));
      loadAsset(
        this.load.svg('whole_note_mask', 'whole_note_mask.svg', {
          scale: scale * 1.3,
        }),
      );
      loadAsset(
        this.load.svg('quarter_rest', 'quarter_rest.svg', {
          scale: scale * 1.3,
        }),
      );
      loadAsset(this.load.svg('half_rest', 'half_rest.svg', { scale: scale * 1.3 }));
      loadAsset(this.load.svg('whole_rest', 'whole_rest.svg', { scale: scale * 1.3 }));
      loadAsset(this.load.svg('quarter_rest', 'quarter_rest.svg', { scale: scale * 1.3 }));
      loadAsset(this.load.svg('half_rest', 'half_rest.svg', { scale: scale * 1.3 }));
      loadAsset(this.load.svg('whole_rest', 'whole_rest.svg', { scale: scale * 1.3 }));
      // The checkmark and crossmark graphics were exported at a higher resolution but instead of fixing it
      // by re-exporting the files themselves, we fix it by scaling them down during import cause we can :)
      loadAsset(this.load.svg('checkmark', 'checkmark.svg', { scale: 0.55 * scale }));
      loadAsset(this.load.svg('crossmark', 'crossmark.svg', { scale: 0.4 * scale }));
      loadAsset(this.load.svg('skip_button', 'skip_button.svg', { scale: 1 * scale }));
      /*this.load.script(
        "webfont",
        "https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js"
      );*/
      // Native effects are played by MuseFlowAudio from bundled files. Avoid
      // fetching and decoding the same 43 clips into WKWebView/Phaser memory.
      if (!isCapacitorNative()) {
        this.load.setPath(phaserAssetsRootDir + '/audio');
        loadAsset(this.load.audio('click', 'click.wav'));
        loadAsset(this.load.audio('click-down', 'click-down.wav'));
        loadAsset(this.load.audio('success', 'success.wav'));
        [
          'great',
          'great_job',
          'good_job',
          'nice',
          'excellent',
          'nicely_done',
          'well_done',
          'perfect',
        ].flatMap((type, typeIdx) =>
          Range(1, 6).forEach((idx) =>
            loadAsset(this.load.audio(`feedback${typeIdx}_0${idx}`, `${type}_0${idx}.wav`)),
          ),
        );
      }
    };
    load();
  }

  create() {
    //  When all the assets have loaded, it's often worth creating global objects here that the rest of the game can use.
    //  For example, you can define global animations here, so we can use them in other scenes.
    // We start with the scene with the piano and then use the `changeScene()` method in the Exercise class to switch
    // to the scene containing the first exercise.
    this.startNextScene();
  }

  // Advance to the next scene exactly once, whether we arrived from a normal
  // create() (all assets settled) or from the preload watchdog (MUS-435 degraded
  // start). The guard prevents a late create() from double-starting the scene after
  // the watchdog has already advanced.
  private startNextScene() {
    if (this.nextSceneStarted) {
      return;
    }
    this.nextSceneStarted = true;
    this.clearPreloadWatchdog();
    this.scene.start('Empty', {});
  }

  private clearPreloadWatchdog() {
    if (this.preloadWatchdog !== undefined) {
      clearTimeout(this.preloadWatchdog);
      this.preloadWatchdog = undefined;
    }
  }
}
